import React, { useEffect, useMemo, useState } from "react";
import {
  EuiBasicTable,
  EuiBasicTableColumn,
  EuiButton,
  EuiButtonEmpty,
  EuiCallOut,
  EuiCode,
  EuiFieldNumber,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormRow,
  EuiLink,
  EuiModal,
  EuiModalBody,
  EuiModalFooter,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiSpacer,
  EuiText,
  EuiTextArea,
} from "@elastic/eui";
import { useApi } from "src/api";
import {
  kitShortfall,
  OpsanioHardwareKind,
  OpsanioHardwareSpec,
  OpsanioHardwareSummary,
  OpsanioKitLine,
  OPSANIO_APPLIANCE_KIT,
  useAnketaAuth,
} from "src/auth";
import { useAnketaI18n } from "src/internationalization";
import { useToast } from "src/notifications";

const i18n = "anketa.opsanio.overview.hardware.request";

interface Row extends OpsanioKitLine {
  quantity: number;
  /** Gross requirement for the chosen appliance count. */
  needed: number;
  /** Free stock netted off, `null` when unknown. */
  free: number | null;
  /** The NetBox device type this line is ordered by; null while loading. */
  spec: OpsanioHardwareSpec | null;
  /** False when the spec is the kit's fallback rather than live NetBox data. */
  fromNetBox: boolean;
  /** NetBox device list this line's free figure was counted from; "" if none. */
  freeUrl: string;
}

/** One line of `GET /netbox/hardware/kit`. */
interface KitSpecLine {
  key: string;
  spec: OpsanioHardwareSpec | null;
  fromNetBox: boolean;
  /** The filtered NetBox device lists behind this line's stock figures. */
  stock?: { freeUrl: string; totalUrl: string };
}

/**
 * Order form for new opsanio hardware, mailed to inside sales.
 *
 * Opens pre-filled with one complete appliance (3 servers, 1 switch, 6 DAC, 6
 * power cables) because that is what is actually ordered; the appliance
 * multiplier rewrites every line at once, and each line stays individually
 * editable for the "we only need cables" case. A line set to 0 is simply not
 * ordered.
 *
 * The free/total figures sit next to each line so the decision and the numbers
 * behind it are on one screen. Power cables are not NetBox devices, so their
 * stock reads "not tracked" - never "0 free", which would imply an outage.
 */
export const HardwareRequestModal = ({
  summary,
  failed,
  onClose,
}: {
  summary: OpsanioHardwareSummary;
  /** Kinds NetBox could not answer for - their stock is unknown, not zero. */
  failed: string[];
  onClose: () => void;
}): JSX.Element => {
  const { Get, Post } = useApi();
  const { keycloak } = useAnketaAuth();
  const { translate } = useAnketaI18n();
  const { addToast } = useToast();

  const [appliances, setAppliances] = useState<number>(1);
  const [quantities, setQuantities] = useState<Record<string, number>>(() =>
    Object.fromEntries(
      kitShortfall(1, summary, failed as OpsanioHardwareKind[]).map((line) => [
        line.key,
        line.order,
      ]),
    ),
  );
  const [note, setNote] = useState<string>("");
  const [sending, setSending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  /** Device types per kit key, as the server resolved them. */
  const [specs, setSpecs] = useState<Record<string, KitSpecLine>>({});

  // What the mail will actually quote. Fetched rather than derived in the
  // browser: the device types live behind admin settings the UI has no access
  // to, and an order dialog that shows a different part number than the mail
  // is worse than one that shows none.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const response = await Get<any>({
        path: "v2/netbox/hardware/kit",
        responseType: "json",
        token: keycloak?.token,
      });
      // The API client never throws on HTTP errors - `success` is the check.
      if (cancelled || !response.success) return;
      const lines: KitSpecLine[] = (response.data as any)?.data?.lines ?? [];
      setSpecs(Object.fromEntries(lines.map((line) => [line.key, line])));
    };
    void load();
    return () => {
      cancelled = true;
    };
    // `Get` is re-created on every render; re-running on it would refetch the
    // kit on each keystroke in the note field.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keycloak?.token]);

  const rows = useMemo<Row[]>(() => {
    const byKey = Object.fromEntries(
      kitShortfall(appliances, summary, failed as OpsanioHardwareKind[]).map(
        (line) => [line.key, line],
      ),
    );
    return OPSANIO_APPLIANCE_KIT.map((line) => ({
      ...line,
      quantity: quantities[line.key] ?? 0,
      needed: byKey[line.key]?.needed ?? 0,
      free: byKey[line.key]?.free ?? null,
      // Until the lookup answers, the kit's own spec - never nothing, so the
      // C13/C14 length is on screen from the first frame.
      spec: specs[line.key]?.spec ?? line.spec ?? null,
      fromNetBox: specs[line.key]?.fromNetBox ?? false,
      freeUrl: specs[line.key]?.stock?.freeUrl ?? "",
    }));
  }, [quantities, appliances, summary, failed, specs]);

  const totalItems = rows.reduce((sum, row) => sum + row.quantity, 0);

  /** Set every line to what is still missing for `count` appliances. */
  const applyKit = (count: number) => {
    setAppliances(count);
    setQuantities(
      Object.fromEntries(
        kitShortfall(count, summary, failed as OpsanioHardwareKind[]).map(
          (line) => [line.key, line.order],
        ),
      ),
    );
  };

  const send = async () => {
    setSending(true);
    setError(null);
    const response = await Post<any>({
      path: "v2/netbox/hardware/request",
      value: {
        lines: rows
          .filter((row) => row.quantity > 0)
          .map((row) => ({ key: row.key, quantity: row.quantity })),
        note,
      },
      responseType: "json",
      token: keycloak?.token,
    });
    setSending(false);

    // The API client never throws on HTTP errors - `success` is the check.
    if (!response.success || response.error) {
      setError(
        response.error?.message ??
          translate(`${i18n}.error`, {
            defaultMessage: "The hardware request could not be sent.",
          }),
      );
      return;
    }

    addToast({
      title: translate(`${i18n}.sent.title`, {
        defaultMessage: "Hardware requested",
      }),
      color: "success",
      text: (
        <p>
          {translate(`${i18n}.sent.body`, {
            values: { count: totalItems },
            defaultMessage: "{{count}} items were requested from inside sales",
          })}
        </p>
      ),
    });
    onClose();
  };

  const stockLabel = (row: Row): string => {
    if (row.kind === null) {
      return translate(`${i18n}.stock.untracked`, {
        defaultMessage: "not tracked in NetBox",
      });
    }
    if (failed.includes(row.kind)) {
      return translate(`${i18n}.stock.unknown`, {
        defaultMessage: "unknown (NetBox lookup failed)",
      });
    }
    const counts = summary[row.kind];
    return translate(`${i18n}.stock.value`, {
      values: { free: counts.free, total: counts.total },
      defaultMessage: "{{free}} free of {{total}}",
    });
  };

  const columns: Array<EuiBasicTableColumn<Row>> = [
    {
      name: translate(`${i18n}.column.item`, { defaultMessage: "Item" }),
      render: (row: Row) => (
        <>
          {/*
            The model links into NetBox, so everything the dialog does not show
            - ports, weight, the devices of this type - is one click away. No
            URL means no link rather than a dead one: the power cable is no
            device type at all.
          */}
          <EuiText size="s">
            {row.spec?.url ? (
              <EuiLink href={row.spec.url} target="_blank" external>
                <strong>{row.spec.model || row.label}</strong>
              </EuiLink>
            ) : (
              <strong>{row.spec?.model || row.label}</strong>
            )}
          </EuiText>
          {/*
            The part number is what inside sales orders on, so it is shown here
            and not only in the mail. A line whose device type carries none says
            nothing rather than showing an empty code block.
          */}
          {row.spec?.partNumber && (
            <EuiText size="xs">
              <EuiCode>{row.spec.partNumber}</EuiCode>
            </EuiText>
          )}
          {row.spec?.description && (
            <EuiText size="xs" color="subdued">
              {row.spec.description}
            </EuiText>
          )}
          {/*
            Where the spec came from. A static fallback is legitimate (power
            cables are no device type) but must not read as live NetBox data.
          */}
          {row.spec?.fallbackReason && (
            <EuiText size="xs" color="warning">
              {row.spec.fallbackReason}
            </EuiText>
          )}
          <EuiText size="xs" color="subdued">
            {translate(`${i18n}.perAppliance`, {
              values: { count: row.perAppliance },
              defaultMessage: "{{count}} per appliance",
            })}
          </EuiText>
        </>
      ),
    },
    {
      name: translate(`${i18n}.column.stock`, { defaultMessage: "In stock" }),
      width: "240px",
      render: (row: Row) => (
        <>
          {/*
            The figure links to the NetBox list it was counted from, filtered
            the same way - checking "3 free of 8" should not mean rebuilding
            the tag and role filters by hand.
          */}
          <EuiText size="xs" color="subdued">
            {row.freeUrl ? (
              <EuiLink href={row.freeUrl} target="_blank" external>
                {stockLabel(row)}
              </EuiLink>
            ) : (
              stockLabel(row)
            )}
          </EuiText>
          {/*
            Show the arithmetic behind the pre-filled quantity, so "0 servers"
            reads as "already covered" rather than as a bug.
          */}
          {row.needed > 0 && (
            <EuiText size="xs" color={row.free === null ? "subdued" : "success"}>
              {row.free === null
                ? translate(`${i18n}.netting.gross`, {
                    values: { needed: row.needed },
                    defaultMessage: "needs {{needed}}, stock unknown → order all",
                  })
                : translate(`${i18n}.netting.net`, {
                    values: {
                      needed: row.needed,
                      free: Math.min(row.free, row.needed),
                    },
                    defaultMessage:
                      "needs {{needed}}, {{free}} covered from stock",
                  })}
            </EuiText>
          )}
        </>
      ),
    },
    {
      name: translate(`${i18n}.column.order`, { defaultMessage: "Order" }),
      width: "130px",
      render: (row: Row) => (
        <EuiFieldNumber
          compressed
          min={0}
          max={999}
          value={row.quantity}
          aria-label={row.label}
          onChange={(e) => {
            const next = parseInt(e.target.value, 10);
            setQuantities((prev) => ({
              ...prev,
              [row.key]: Number.isFinite(next) && next > 0 ? next : 0,
            }));
          }}
        />
      ),
    },
  ];

  return (
    <EuiModal onClose={onClose} style={{ minWidth: 680 }}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          <h2>
            {translate(`${i18n}.modal.title`, {
              defaultMessage: "Request hardware",
            })}
          </h2>
        </EuiModalHeaderTitle>
      </EuiModalHeader>
      <EuiModalBody>
        <EuiText size="s">
          <p>
            {translate(`${i18n}.modal.body`, {
              defaultMessage:
                "Sends an order request to inside sales. Part number, model name and description come from the NetBox device types configured in Administration; the current NetBox stock is attached automatically.",
            })}
          </p>
        </EuiText>

        <EuiSpacer size="m" />
        <EuiFlexGroup alignItems="flexEnd" gutterSize="m" responsive={false}>
          <EuiFlexItem grow={false} style={{ width: 160 }}>
            <EuiFormRow
              label={translate(`${i18n}.appliances`, {
                defaultMessage: "Complete appliances",
              })}
            >
              <EuiFieldNumber
                min={0}
                max={99}
                value={appliances}
                onChange={(e) => {
                  const next = parseInt(e.target.value, 10);
                  applyKit(Number.isFinite(next) && next > 0 ? next : 0);
                }}
              />
            </EuiFormRow>
          </EuiFlexItem>
          <EuiFlexItem>
            <EuiText size="xs" color="subdued">
              {translate(`${i18n}.appliances.hint`, {
                defaultMessage:
                  "Fills every line with what is still missing for that many appliances - free hardware is used up first, so nothing already in stock is ordered again. Adjust individual lines afterwards.",
              })}
            </EuiText>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size="m" />
        <EuiBasicTable<Row> items={rows} columns={columns} tableLayout="auto" />

        <EuiSpacer size="m" />
        <EuiFormRow
          label={translate(`${i18n}.note`, { defaultMessage: "Note" })}
          fullWidth
        >
          <EuiTextArea
            fullWidth
            compressed
            rows={3}
            value={note}
            maxLength={2000}
            placeholder={translate(`${i18n}.note.placeholder`, {
              defaultMessage: "Deadline, customer, ticket reference…",
            })}
            onChange={(e) => setNote(e.target.value)}
          />
        </EuiFormRow>

        {error && (
          <>
            <EuiSpacer size="s" />
            <EuiCallOut size="s" color="danger" iconType="alert" title={error} />
          </>
        )}
      </EuiModalBody>
      <EuiModalFooter>
        <EuiButtonEmpty onClick={onClose}>
          {translate("anketa.create.action.cancel", {
            defaultMessage: "Cancel",
          })}
        </EuiButtonEmpty>
        <EuiButton
          fill
          iconType="email"
          onClick={send}
          isLoading={sending}
          isDisabled={totalItems === 0}
        >
          {translate(`${i18n}.send`, {
            values: { count: totalItems },
            defaultMessage: "Request {{count}} items",
          })}
        </EuiButton>
      </EuiModalFooter>
    </EuiModal>
  );
};

export default HardwareRequestModal;
